// API Service for Redux Sagas
// This provides the method-based API interface expected by sagas

import apisauce from 'apisauce'
import store from 'store'

// Storage keys (matching appointo_client pattern)
const AUTH_DATA_KEY = 'authData'
const TEAM_KEY = 'video_panda_team'

// Create API module
const create = () => {
  // In monorepo, API is served from same domain (no separate API base URL needed)
  const baseURL = ''
  
  const api = apisauce.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 60000,
  })

  // ============ Auth Header Management ============
  const setAuthData = (authData) => {
    if (authData?.token) {
      api.setHeader('Authorization', `Bearer ${authData.token}`)
    }
    if (authData?.teamId) {
      api.setHeader('X-Team-Id', authData.teamId)
    }
    return api
  }

  const deleteAuthData = () => {
    api.deleteHeader('Authorization')
    api.deleteHeader('X-Team-Id')
    return api
  }

  const setTeamId = (teamId) => {
    if (teamId) {
      api.setHeader('X-Team-Id', teamId)
    } else {
      api.deleteHeader('X-Team-Id')
    }
  }

  // Initialize auth from stored data
  const initializeAuth = () => {
    const authData = store.get(AUTH_DATA_KEY)
    const teamId = store.get(TEAM_KEY)
    if (authData?.token) {
      api.setHeader('Authorization', `Bearer ${authData.token}`)
    }
    if (teamId) {
      api.setHeader('X-Team-Id', teamId)
    }
  }

  // Call on creation
  initializeAuth()

  // ============ Auth API ============
  // Backend expects params wrapped in 'user' object
  const signUp = (params) => api.post('/api/v1/auth/sign_up', { 
    user: {
      email: params.email,
      password: params.password,
      password_confirmation: params.passwordConfirmation || params.password_confirmation || params.password,
      first_name: params.firstName || params.first_name,
      last_name: params.lastName || params.last_name,
    }
  })
  const signIn = (params) => api.post('/api/v1/auth/sign_in', { 
    user: { 
      email: params.email, 
      password: params.password 
    } 
  })
  const signOut = () => api.delete('/api/v1/auth/sign_out')
  const getCurrentUser = () => api.get('/api/v1/auth/me')

  // ============ Scripts API ============
  const getScripts = (params) => api.get('/scripts', params)
  const getScript = (id) => api.get(`/scripts/${id}`)
  const getScriptStatus = (id) => api.get(`/scripts/${id}/status`)
  const createScript = (params) => api.post('/scripts', { script: params })
  const createScriptWithFormData = (formData) => api.post('/scripts', formData)
  const updateScript = (id, params) => api.patch(`/scripts/${id}`, { script: params })
  const getSurpriseScript = () => api.get('/scripts/surprise')
  const getPromptIdeas = (params) => api.get('/scripts/prompt_ideas', params)
  const getHooks = (params) => api.get('/scripts/generate_hooks', params)
  const regenerateScript = (id) => api.post(`/scripts/${id}/regenerate`)

  // ============ Audio API ============
  const createAudio = (scriptId, params) => api.post(`/scripts/${scriptId}/audios`, params ? { audio: params } : undefined)
  const getAudio = (id) => api.get(`/audios/${id}`)
  const getAudioStatus = (id) => api.get(`/audios/${id}/status`)
  const getAudiosForScript = (scriptId) => api.get(`/scripts/${scriptId}/audios`)

  // ============ Video API ============
  const createVideo = (scriptId, params) => api.post(`/scripts/${scriptId}/videos`, { video: params })
  const getVideo = (id) => api.get(`/videos/${id}`)
  const getVideoStatus = (id) => api.get(`/videos/${id}/status`)
  const getVideosForScript = (scriptId) => api.get(`/scripts/${scriptId}/videos`)

  // ============ Draft Segments API ============
  const saveDraftSegments = (scriptId, params) => api.put(`/scripts/${scriptId}/draft_segments`, { draft: params })
  const getDraftSegments = (scriptId) => api.get(`/scripts/${scriptId}/draft_segments`)
  const deleteDraftSegments = (scriptId) => api.delete(`/scripts/${scriptId}/draft_segments`)

  // ============ Thumbnail Image API ============
  const regenerateThumbnailImage = (thumbnailId, params) => api.post(`/thumbnail_images/${thumbnailId}/regenerate`, params)
  const getThumbnailImageStatus = (thumbnailId) => api.get(`/thumbnail_images/${thumbnailId}/status`)
  const getThumbnailImageHistory = (thumbnailId) => api.get(`/thumbnail_images/${thumbnailId}/history`)
  const selectThumbnailFromHistory = (thumbnailId, params) => api.post(`/thumbnail_images/${thumbnailId}/select_from_history`, params)

  // ============ Animation API ============
  const generateAnimation = (thumbnailId, params) => api.post(`/thumbnail_images/${thumbnailId}/animation`, params)
  const getAnimationStatus = (thumbnailId) => api.get(`/thumbnail_images/${thumbnailId}/animation`)
  const regenerateAnimation = (thumbnailId) => api.post(`/thumbnail_images/${thumbnailId}/animation/regenerate`)
  const skipAnimation = (thumbnailId) => api.post(`/thumbnail_images/${thumbnailId}/animation/skip`)
  const batchGenerateAnimations = (scriptId) => api.post(`/scripts/${scriptId}/animations/batch_generate`)
  const getBatchAnimationStatus = (scriptId) => api.get(`/scripts/${scriptId}/animations/batch_status`)

  // ============ Subtitle API ============
  const getSubtitleThemes = () => api.get('/subtitle_themes')
  const transcribeAudio = (audioId) => api.post(`/audios/${audioId}/transcribe`)
  const regenerateSubtitles = (audioId, style) => api.post(`/audios/${audioId}/regenerate_subtitles`, { style })

  // ============ Voice API ============
  const getVoices = () => api.get('/voices')

  // ============ Sound Effects API ============
  const searchSoundEffects = (query, options) => api.get('/sound_effects/search', { query, ...options })
  const getSoundEffectCategories = () => api.get('/sound_effects/categories')
  const getVideoSoundEffectClips = (videoId) => api.get(`/videos/${videoId}/sound_effect_clips`)
  const addSoundEffectClip = (videoId, clipData) => api.post(`/videos/${videoId}/sound_effect_clips`, clipData)
  const updateSoundEffectClip = (videoId, clipId, updates) => api.patch(`/videos/${videoId}/sound_effect_clips/${clipId}`, updates)
  const deleteSoundEffectClip = (videoId, clipId) => api.delete(`/videos/${videoId}/sound_effect_clips/${clipId}`)

  // ============ Publications API ============
  const generatePublishMetadataByScript = (scriptId) => api.post(`/scripts/${scriptId}/generate_publish_metadata`)
  const savePublishMetadata = (scriptId, platforms) => api.post(`/scripts/${scriptId}/save_publish_metadata`, { platforms })
  const loadPublishMetadata = (scriptId) => api.get(`/scripts/${scriptId}/load_publish_metadata`)
  const generatePublishMetadata = (videoId) => api.post(`/videos/${videoId}/video_publications/generate_metadata`)
  const createPublication = (videoId, params) => api.post(`/videos/${videoId}/video_publications`, { publication: params })
  const sendPublicationEmail = (videoId, publicationId, email) => api.post(`/videos/${videoId}/video_publications/${publicationId}/send_email`, { email })
  const getPublications = (videoId) => api.get(`/videos/${videoId}/video_publications`)

  // ============ Credits API ============
  const getCredits = () => api.get('/credits')
  const previewCredits = (params) => api.post('/credits/preview', params)
  const checkCredits = (params) => api.post('/credits/check', params)
  const getCreditHistory = (params) => api.get('/credits/history', params)

  // ============ Plans API ============
  const getPlans = () => api.get('/plans')
  const getCurrentPlan = () => api.get('/plans/current')
  const upgradePlan = (params) => api.post('/plans/upgrade', params)
  const subscribePlan = (params) => api.post('/plans/subscribe', params)
  const downgradePlan = () => api.post('/plans/downgrade')
  const reactivatePlan = () => api.post('/plans/reactivate')
  const comparePlans = () => api.get('/plans/compare')
  const getCreditPacks = () => api.get('/plans/credit_packs')
  const buyCredits = (params) => api.post('/plans/buy_credits', params)

  // ============ Return API Object ============
  return {
    // Auth management
    setAuthData,
    deleteAuthData,
    setTeamId,
    initializeAuth,

    // Auth
    signUp,
    signIn,
    signOut,
    getCurrentUser,

    // Scripts
    getScripts,
    getScript,
    getScriptStatus,
    createScript,
    createScriptWithFormData,
    updateScript,
    getSurpriseScript,
    getPromptIdeas,
    getHooks,
    regenerateScript,

    // Audio
    createAudio,
    getAudio,
    getAudioStatus,
    getAudiosForScript,

    // Video
    createVideo,
    getVideo,
    getVideoStatus,
    getVideosForScript,

    // Draft Segments
    saveDraftSegments,
    getDraftSegments,
    deleteDraftSegments,

    // Thumbnail Images
    regenerateThumbnailImage,
    getThumbnailImageStatus,
    getThumbnailImageHistory,
    selectThumbnailFromHistory,

    // Animations
    generateAnimation,
    getAnimationStatus,
    regenerateAnimation,
    skipAnimation,
    batchGenerateAnimations,
    getBatchAnimationStatus,

    // Subtitles
    getSubtitleThemes,
    transcribeAudio,
    regenerateSubtitles,

    // Voices
    getVoices,

    // Sound Effects
    searchSoundEffects,
    getSoundEffectCategories,
    getVideoSoundEffectClips,
    addSoundEffectClip,
    updateSoundEffectClip,
    deleteSoundEffectClip,

    // Publications
    generatePublishMetadataByScript,
    savePublishMetadata,
    loadPublishMetadata,
    generatePublishMetadata,
    createPublication,
    sendPublicationEmail,
    getPublications,

    // Credits
    getCredits,
    previewCredits,
    checkCredits,
    getCreditHistory,

    // Plans
    getPlans,
    getCurrentPlan,
    upgradePlan,
    subscribePlan,
    downgradePlan,
    reactivatePlan,
    comparePlans,
    getCreditPacks,
    buyCredits,
  }
}

export default { create }
